import { forwardRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import { chain, size } from "lodash"
import classNames from "classnames"
import { Collapse, IconButton } from "@mui/material"
import { Add, Remove } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import TitleRow from "./title-row"
import FactorRow from "./factor-row"
import { getToggleAllTitle } from "./helpers"

const Category = forwardRef(({ label, color = "inherit", factors, getter, getValues, valueClassNames, getColumnColorStyle, maxLabelWidth, opened, singleFactor }, ref) => {
  const [expanded, setExpanded] = useState(false)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    setExpanded(opened)
  }, [opened])

  const handleClick = () => {
    const newValue = !expanded
    setExpanded(newValue)
    if (!newValue) {
      setShowAll(false)
    }
  }
  const handleToggleAll = (e) => {
    e.stopPropagation()
    setShowAll(!showAll)
  }

  const fixed = singleFactor || !size(factors)
  const scrollable = size(factors) > 10

  // Instead of passing multiple props to be able to define this button inside of TitleRow, we define it here instead
  // since this component is in charge of keeping track of the various Category states anyway [collapsed, expanded, expanded all]
  const ToggleAllButton = <Tooltip title={getToggleAllTitle(showAll, scrollable, label)}>
    <span>
      {/* span is required because button can be disabled in tooltip, which prevents events */}
      <IconButton disabled={!scrollable} size="small" className="toggle-all" onClick={handleToggleAll}>
        {!showAll ? <Add /> : <Remove />}
      </IconButton>
    </span>
  </Tooltip>

  return <div className={classNames("factor-category", { expanded, expandable: !fixed, fixed })}>
    <Collapse collapsedSize={48} in={expanded}>
      <div className="colored-border" style={{ backgroundColor: color }} />
      <TitleRow ref={ref} {...{ label, color, fixed, values: getValues(getter), valueClassNames, getColumnColorStyle, maxLabelWidth, ToggleAllButton }} onClick={!fixed ? handleClick : undefined} />
      <div className={classNames("factors", { scroll: size(factors) > 10, all: showAll })}>
        {chain(factors)
          .map(factor => <FactorRow key={factor.id} {...{ factor, fixed, categoryColor: color, values: getValues(factor.getter), valueClassNames, getColumnColorStyle, maxLabelWidth }} />)
          .value()}
      </div>
    </Collapse>
  </div>
})

Category.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  factors: PropTypes.array,
  getter: PropTypes.func,
  getValues: PropTypes.func.isRequired,
  valueClassNames: PropTypes.array,
  getColumnColorStyle: PropTypes.func,
  maxLabelWidth: PropTypes.number,
  opened: PropTypes.bool,
  singleFactor: PropTypes.bool,
}

Category.displayName = "Category"

export default Category
